import React, { useState } from "react";
import PropTypes from "prop-types";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
	CircularProgress
} from "@mui/material";
import { Translate } from "orion-components/i18n";
import { withStyles } from "@mui/styles";

const propTypes = {
	qaId: PropTypes.string,
	open: PropTypes.bool.isRequired,
	title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	textContent: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	confirm: PropTypes.shape({
		label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
		action: PropTypes.func.isRequired,
		disabled: PropTypes.bool,
		style: PropTypes.object,
		buttonVariant: PropTypes.string
	}),
	abort: PropTypes.shape({
		label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
		action: PropTypes.func.isRequired,
		disabled: PropTypes.bool,
		style: PropTypes.object
	}),
	deletion: PropTypes.shape({
		label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
		action: PropTypes.func.isRequired,
		disabled: PropTypes.bool
	}),
	textFooter: PropTypes.string,
	requesting: PropTypes.bool,
	deleteConfirmation: PropTypes.bool,
	deleteConfirmationLabel: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	// The options prop allows you to pass in any Material-ui Dialog prop
	// Found here: https://material-ui.com/api/dialog/#props
	options: PropTypes.shape({
		disableBackdropClick: PropTypes.bool,
		disableEscapeKeyDown: PropTypes.bool,
		fullScreen: PropTypes.bool,
		fullWidth: PropTypes.bool,
		maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
		onBackdropClick: PropTypes.func,
		onClose: PropTypes.func,
		onEnter: PropTypes.func,
		onEntered: PropTypes.func,
		onEntering: PropTypes.func,
		onEscapeKeyDown: PropTypes.func,
		onExit: PropTypes.func,
		onExited: PropTypes.func,
		onExiting: PropTypes.func,
		dir: PropTypes.string
	}),
	paperPropStyles: PropTypes.object,
	titlePropStyles: PropTypes.object,
	dir: PropTypes.string,
	classes: PropTypes.object,
	dialogContentStyles: PropTypes.object,
	actionPropStyles: PropTypes.object,
	actions: PropTypes.bool,
	DialogActionsFunction: PropTypes.func,
	dialogContentTextStyles: PropTypes.object,
	actionContainerPropStyles: PropTypes.object,
	dialogSX: PropTypes.object,
	onClose: PropTypes.func
};

const defaultProps = {
	qaId: "",
	title: "",
	children: [],
	textContent: "",
	confirm: { buttonVariant: "text" },
	abort: null,
	deletion: null,
	textFooter: "",
	requesting: false,
	deleteConfirmation: true,
	deleteConfirmationLabel: null,
	actionPropStyles: {},
	actions: false,
	DialogActionsFunction: () => { },
	dialogContentTextStyles: {},
	actionContainerPropStyles: {},
	dialogSX: {},
	onClose: () => { }
};

const stopPropagation = (e) => {
	e.stopPropagation();
};

const confirmButtonText = (id) => {
	switch(id){
		case "user-new":
			return "done"
		case "role-new":
			return "add"
		default:
			return "confirm"
	}
}

const styleOverrides = {
	disabled: {
		color: "#fff!important",
		opacity: "0.3"
	}
};

const CBDialog = ({
	qaId,
	open,
	title,
	children,
	textContent,
	confirm,
	abort,
	deletion,
	textFooter,
	requesting,
	deleteConfirmation,
	deleteConfirmationLabel,
	options,
	paperPropStyles,
	titlePropStyles,
	dir,
	classes,
	dialogContentStyles,
	actionPropStyles,
	actions,
	DialogActionsFunction,
	dialogContentTextStyles,
	actionContainerPropStyles,
	dialogSX,
	onClose,
	dismiss
}) => {
	const [deletionConfirm, setDeletionConfirm] = useState(false);

	const styles = {
		dialog: { backgroundColor: "#2c2d2f" },
		title: { color: "#fff", letterSpacing: "unset", lineHeight: "unset" },
		confirm: { color: "#35b7f3" },
		footer: { margin: "32px 0" },
		deleteConfirmationBtn: {
			color: deletionConfirm ? "#FFF" : "#E85858",
			"&.MuiButton-root": {
				background: deletionConfirm ? "#E85858" : ""
			},
			...(dir === "ltr" && { marginRight: "auto" })
		},
		deleteBtn: {
			color: "#E85858",
			...(dir === "ltr" && { marginRight: "auto" }),
			...(dir === "rtl" && { marginLeft: "auto" })
		}
	};

	const askForDeleteConfirmation = () => {
		setDeletionConfirm(true);
		setTimeout(() => {
			setDeletionConfirm(false);
		}, 5000);
	};

	return (
		<Dialog
			data-testid="cb-dialog"
			{...options}
			PaperProps={{
				sx: {
					...styles.dialog,
					...(paperPropStyles ? paperPropStyles : {})
				}
			}}
			open={open}
			disableEnforceFocus={true}
			scroll="paper"
			onMouseDown={stopPropagation}
			onTouchStart={stopPropagation}
			sx={dialogSX}
			onClose={onClose}
		>
			{title && (
				<DialogTitle sx={{ ...styles.title, ...titlePropStyles }} disableTypography={true} data-testid="orion-components-cbcomponents-cbdialog-cbdialog-dialog-title">
					{title}
				</DialogTitle>
			)}
			<DialogContent sx={dialogContentStyles} data-testid="cb-dialog-content">
				{textContent && (
					<DialogContentText variant="body2" color="#fff" sx={{ ...dialogContentTextStyles }}>
						{textContent}
					</DialogContentText>
				)}
				{children}
				{textFooter && (
					<DialogContentText variant="body2" style={styles.footer}>
						{textFooter}
					</DialogContentText>
				)}
			</DialogContent>
			{actions ? (
				DialogActionsFunction()
			) : (
				<DialogActions sx={{ ...actionContainerPropStyles }} data-testid="cb-dialog-actions">
					{deletion &&
						!requesting &&
						(deleteConfirmation ? (
							<Button
								data-testid={(qaId ? qaId + "-confirm-btn" : "")}
								sx={styles.deleteConfirmationBtn}
								onClick={deletionConfirm ? deletion.action : askForDeleteConfirmation}
								variant={deletionConfirm ? "contained" : "text"}
								onMouseDown={stopPropagation}
								onTouchStart={stopPropagation}
							>
								{deletionConfirm ? (
									deleteConfirmationLabel || <Translate value="global.CBComponents.CBDialog.confirmDelete" />
								) : (
									deletion.label
								)}
							</Button>
						) : (
							<Button
								data-testid={(qaId ? qaId + "-delete-btn" : "")}
								sx={styles.deleteBtn}
								onClick={deletion.action}
								onMouseDown={stopPropagation}
								onTouchStart={stopPropagation}
								style={actionPropStyles}
							>
								{deletion.label}
							</Button>
						))}
					{abort && !requesting && (
						<Button
							onClick={abort.action}
							onMouseDown={stopPropagation}
							onTouchStart={stopPropagation}
							style={actionPropStyles}
							sx={{
								color: abort.style ? abort.style : "#B3B7BC",
								...actionPropStyles
							}} data-testid="orion-components-cbcomponents-cbdialog-cbdialog-button"
						>
							{abort.label}
						</Button>
					)}
					{confirm && !requesting && (
						<Button
							data-testid={(qaId ? qaId + "-" + confirmButtonText(qaId) + "-btn" : "")}
							disabled={confirm.disabled}
							onClick={confirm.action}
							onMouseDown={stopPropagation}
							onTouchStart={stopPropagation}
							sx={{
								...actionPropStyles,
								...(confirm.buttonVariant === "contained" && { color: "#FFFFFF" })
							}}
							style={confirm.style}
							classes={{
								disabled: classes.disabled
							}}
							variant={confirm.buttonVariant}
						>
							{confirm.label}
						</Button>
					)}
					{dismiss && !requesting && (
						<Button
							disabled={dismiss.disabled}
							onClick={dismiss.action}
							onMouseDown={stopPropagation}
							onTouchStart={stopPropagation}
							sx={{
								...actionPropStyles,
								...(dismiss.buttonVariant === "contained" && { color: "#FFFFFF" })
							}}
							style={dismiss.style}
							classes={{
								disabled: classes.disabled
							}}
							variant={dismiss.buttonVariant} data-testid="orion-components-cbcomponents-cbdialog-cbdialog-button-2"
						>
							{dismiss.label}
						</Button>
					)}
					{requesting && <CircularProgress data-testid="orion-components-cbcomponents-cbdialog-cbdialog-circular-progress" />}
				</DialogActions>
			)}
		</Dialog>
	);
};

CBDialog.propTypes = propTypes;
CBDialog.defaultProps = defaultProps;

export default withStyles(styleOverrides)(CBDialog);
